import type { PayloadAction } from '@reduxjs/toolkit'
import { createSlice } from '@reduxjs/toolkit'

import type { RootState } from '@web/app/store'
import type { IServer } from '@web/entities/server/model/server.type'
import { useDispatchedActions } from '@web/shared/hooks/use-dispatched-actions.hook'
import { serverStorage } from '@web/shared/local-storage/servers.storage'

export interface ISettingsState {
  version: string
  isDesktop: boolean
  servers: IServer[]
  activeServer: IServer | null
}

const initialState: ISettingsState = {
  version: __APP_VERSION__,
  isDesktop: window.isElectron,
  servers: serverStorage.getServers(),
  activeServer: serverStorage.getActiveServerConfig()
}

export const serverSlice = createSlice({
  name: 'server-settings',
  initialState,
  selectors: {
    selectActiveServerUrl: (state) => {
      const { activeServer, isDesktop } = state

      if (!isDesktop) {
        return window.location.origin
      }

      if (!activeServer) return ''

      return activeServer.url
    }
  },
  reducers: {
    addOrUpdateServer(state, action: PayloadAction<IServer>) {
      serverStorage.addOrUpdate(action.payload)
      state.servers = serverStorage.getServers()
    },

    removeServer(state, action: PayloadAction<string>) {
      const idToRemove = action.payload
      const active = serverStorage.getActiveServerConfig()
      serverStorage.remove(idToRemove)
      state.servers = serverStorage.getServers()

      if (active && active.id === idToRemove) {
        serverStorage.clearActive()
        state.activeServer = null
      }
    },

    setActiveServer(state, action: PayloadAction<string>) {
      const found = serverStorage
        .getServers()
        .find((s) => s.id === action.payload)
      if (!found) {
        console.warn(
          `[ServerSettingsSlice] Сервер с id "${action.payload}" не найден.`
        )
        return
      }

      serverStorage.setActiveServerId(found.id)
      state.activeServer = serverStorage.getActiveServerConfig()
    }
  }
})

export const serverSettingsReducer = serverSlice.reducer
export const useServerActions = () => useDispatchedActions(serverSlice.actions)

export const selectActiveServerUrl = (state: RootState): string => {
  const { activeServer, isDesktop } = state.serverSettings
  if (!isDesktop || !activeServer) return ''
  return activeServer.url
}
